import {
  AdminInput,
  AdminSpinner,
  Disclaimer,
  Div,
  Modal,
  P,
  Radio,
  getVDriveFleetOrganizations,
  lightTheme,
  reportError,
  useDebouncedValue,
} from '@vgrubs/components';
import { VDriveFleetOrganization } from 'components/src/types/api/vDrive.types';
import { useEffect, useRef, useState } from 'react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Code of the fleet pinned now, marked in the list. */
  selectedId: string | null;
  onPick: (fleet: VDriveFleetOrganization) => void;
};

/**
 * The list behind the vDrive Fleet field: fleets that deliver only their own
 * restaurant's orders (`deliveryScope=own`), searched on the server by name
 * and address. Picking one closes it; the page's Save sends it.
 */
export const VDriveFleetModal = ({
  isOpen,
  onClose,
  selectedId,
  onPick,
}: Props) => {
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);
  const [fleets, setFleets] = useState<VDriveFleetOrganization[]>([]);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  // Every opening starts from the whole list, not an old search.
  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const request = ++requestRef.current;
    setLoading(true);
    getVDriveFleetOrganizations({
      deliveryScope: 'own',
      ...(query ? { query } : {}),
    })
      .then(({ data }) => {
        if (request === requestRef.current) setFleets(data.data);
      })
      .catch(reportError)
      .finally(() => {
        if (request === requestRef.current) setLoading(false);
      });
  }, [isOpen, query]);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      logo={false}
      header="vDrive Fleet"
    >
      <AdminInput
        search
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value.slice(0, 100))
        }
        placeholder="Search by name or address"
      />

      {/* Room for the spinner before the first answer, and it sits over the
          rows of the last search while the next one loads. */}
      <Div $relative $flex $column $gap="8px" $mt="16" $minHeight="82px">
        {loading && <AdminSpinner small absolute creme />}
        {!loading && fleets.length === 0 && (
          <P $m="16px 0" $center $color={lightTheme.lightGray}>
            {query
              ? 'No fleet matches this search.'
              : 'No vDrive fleets take vGrubs orders yet.'}
          </P>
        )}
        {fleets.map((fleet) => {
          const isSelected = selectedId === fleet.id;
          return (
            <Div
              key={fleet.id}
              $box
              $p="14px 20px"
              $flex
              $alignCenter
              $between
              $gap="16px"
              $pointer
              role="button"
              tabIndex={0}
              onClick={() => onPick(fleet)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPick(fleet);
                }
              }}
            >
              <Div $minWidth="0">
                <P
                  $bold
                  $left
                  $m="0 0 4px"
                  $color={isSelected ? lightTheme.primary : undefined}
                >
                  {fleet.name}
                </P>
                <Disclaimer $m="0" $left $color={lightTheme.darkGray}>
                  {fleet.address || 'No address'} · {fleet.id}
                </Disclaimer>
                {!fleet.quotable && (
                  <Disclaimer $m="4px 0 0" $left $color={lightTheme.darkYellow}>
                    No price list yet
                  </Disclaimer>
                )}
              </Div>
              <Radio checked={isSelected} />
            </Div>
          );
        })}
      </Div>
    </Modal>
  );
};
